import { useMemo } from 'react';
// Utils
import cn from '../../../../utils/cn';
import { parseDisplayNumber } from '../../../../utils/parseDisplayNumber';
// Styles
import styles from './SimpleNumberCell.module.css';

/**
 * Simple number cell component for SimpleTable
 * @param {object} props
 * @param {any} props.value - Numeric value to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.locale='fr-FR'] - Locale for number formatting
 * @param {object} [props.formatOptions] - Intl.NumberFormat options
 * @param {string} [props.prefix] - Prefix to add before the number
 * @param {string} [props.suffix] - Suffix to add after the number
 * @param {'left' | 'center' | 'right'} [props.align='left'] - Text alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @returns {JSX.Element}
 */
function SimpleNumberCell({
  value,
  className = '',
  locale = 'fr-FR',
  formatOptions = {},
  prefix = '',
  suffix = '',
  align = 'left',
  bold = false,
  italic = false,
}) {
  const formattedValue = useMemo(() => {
    if (value === null || value === undefined || value === '') return '';

    const numValue = parseDisplayNumber(value);

    if (Number.isNaN(numValue)) return value;

    try {
      const isPercentStyle = /** @type {any} */ (formatOptions).style === 'percent';
      const valueToFormat = isPercentStyle ? numValue / 100 : numValue;

      return valueToFormat.toLocaleString(locale, formatOptions);
    } catch (error) {
      return numValue.toLocaleString(locale);
    }
  }, [value, locale, formatOptions]);
  const displayValue = useMemo(
    () => `${prefix}${formattedValue}${suffix}`,
    [prefix, formattedValue, suffix],
  );

  return (
    <div
      className={cn([
        styles['simple-number-cell'],
        className,
        bold && styles['simple-number-cell-bold'],
        italic && styles['simple-number-cell-italic'],
        align && styles[`simple-number-cell-align-${align}`],
      ])}
    >
      <span
        className={styles['simple-number-cell-value']}
        title={displayValue || ''}
      >
        {displayValue || ''}
      </span>
    </div>
  );
}

export default SimpleNumberCell;
